// React Imports
import React, { Suspense, useEffect, useState } from 'react';

// Routing
import {
  BrowserRouter, Routes, Route,
  Navigate,
} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

// Styles
import './styles/App.scss';
import { useDispatch, useSelector } from 'react-redux';
import { UPDATE_IS_MOBILE } from './constants';
import DeferredChatBot from './components/ChatBot/DeferredChatBot';
import { ChatBotOpenProvider } from './components/ChatBot/ChatBotOpenContext';
import FullScreenLoader from './components/global/FullScreenLoader';
import { sendQuery, clearSession } from './api/chatApi';

const ListPage = React.lazy(() => import('./pages/List/ListPage'));
const DetailsPage = React.lazy(() => import('./pages/Details/DetailPage'));

function App() {
  const dispatch = useDispatch();
  const [chatSessionId, setChatSessionId] = useState(null);
  // Which partner program tab the user is currently on ('mxp' = Technology, else Solution).
  const programPath = useSelector((state) => state.facets.programPath);
  const primaryBusiness = programPath === 'mxp' ? 'Technology' : 'Solution';

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    dispatch({
      type: UPDATE_IS_MOBILE,
      payload: { isMobile: mql.matches },
    });
    // Optional cleanup
    const handleResize = () => dispatch({
      type: UPDATE_IS_MOBILE,
      payload: { isMobile: mql.matches },
    });
    mql.addListener(handleResize);
    return () => mql.removeListener(handleResize); // Clean up listener on component unmount
  }, []);

  const getBasePath = () => `${window.location.pathname.split('/s/')[0]}/s/directory`;

  return (
    <div className="react-spectrum-provider spectrum spectrum--light spectrum--medium">
      <ChatBotOpenProvider>
        <BrowserRouter basename={getBasePath()}>
          <Suspense fallback={<FullScreenLoader />}>
            <>
              {/* Animated routes (fade in/out for detail and listing page). */}
              <AnimatePresence mode="wait">
                <Routes>
                  <Route path="" element={<Navigate replace to="/solution" />} />
                  <Route path="/:programType/:id" element={<DetailsPage />} />
                  <Route path="/solution" element={<ListPage programType="solution" />} />
                  <Route path="/technology" element={<ListPage programType="technology" />} />
                </Routes>
              </AnimatePresence>
              <DeferredChatBot
                hideTrigger
                triggerLabel="Ask AI Assistant"
                primaryBusiness={primaryBusiness}
                onSendMessage={async (query, reply) => {
                  try {
                    const result = await sendQuery(query, chatSessionId, primaryBusiness);
                    reply(result.response, result.suggestions);
                    setChatSessionId(result.session_id);
                  } catch (err) {
                    const message = err.message ?? 'Something went wrong.';
                    reply(`Sorry, I couldn’t get a response: ${message}`);
                  }
                }}
                onNewConversation={async () => {
                  if (chatSessionId) {
                    try {
                      await clearSession(chatSessionId);
                    } catch {
                      // ignore; server may already have dropped the session
                    }
                  }
                  setChatSessionId(null);
                }}
              />
            </>
          </Suspense>

        </BrowserRouter>
      </ChatBotOpenProvider>
    </div>
  );
}

export default App;
